import React from 'react';
import { resolveTheme, panelStyle } from '../themes';
import type { Theme } from '../themes';

interface ControlsHintProps {
  theme?: string | Theme;
  visible: boolean;
  onDismiss: () => void;
  onShow: () => void;
}

type Row = { keys: string[]; label: string } | { divider: true };

const DESKTOP_ROWS: Row[] = [
  { keys: ['Drag'], label: 'Orbit (left mouse)' },
  { keys: ['R-Drag'], label: 'Pan (right mouse)' },
  { keys: ['Scroll'], label: 'Zoom' },
  { divider: true },
  { keys: ['W', 'A', 'S', 'D'], label: 'Move on ground plane' },
  { keys: ['Q', 'E'], label: 'Move down / up' },
  { keys: ['←', '→', '↑', '↓'], label: 'Look around' },
  { divider: true },
  { keys: ['Space'], label: 'Play / pause timeline' },
  { keys: ['[', ']'], label: 'Prev / next frame' },
];

const TOUCH_ROWS: Row[] = [
  { keys: ['Drag'], label: 'Orbit' },
  { keys: ['2-finger drag'], label: 'Pan' },
  { keys: ['Pinch'], label: 'Zoom' },
];

function useIsTouch() {
  const [touch, setTouch] = React.useState(() =>
    typeof window !== 'undefined' && (
      'ontouchstart' in window || navigator.maxTouchPoints > 0
    ),
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(pointer: coarse)');
    const update = () => setTouch(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return touch;
}

export function ControlsHint({ theme, visible, onDismiss, onShow }: ControlsHintProps) {
  const T = resolveTheme(theme);
  const isTouch = useIsTouch();
  const rows = isTouch ? TOUCH_ROWS : DESKTOP_ROWS;

  if (!visible) {
    return (
      <button
        onClick={onShow}
        aria-expanded={false}
        aria-controls="controls-panel"
        className="controls-hint-toggle"
        style={{
          appearance: 'none', border: `1px solid ${T.border}`,
          background: T.bgPanel, color: T.text,
          font: `11px ${T.font}`, letterSpacing: T.letterSpacing,
          padding: '6px 10px', borderRadius: T.radius, cursor: 'pointer',
          backdropFilter: 'blur(14px)',
        }}
      >? CONTROLS</button>
    );
  }

  return (
    <div id="controls-panel" aria-expanded={true} className="controls-hint-panel" style={panelStyle(T, { padding: '10px 14px 12px' })}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontWeight: T.headerWeight, fontSize: 11, textTransform: 'uppercase' }}>
          {isTouch ? 'Touch Controls' : 'Controls'}
        </span>
        <button onClick={onDismiss} style={{
          marginLeft: 'auto', appearance: 'none', border: 0, background: 'transparent',
          color: T.textDim, cursor: 'pointer', fontSize: 14, lineHeight: 1,
        }}>x</button>
      </div>
      {rows.map((r, i) => 'divider' in r ? (
        <div key={i} style={{ height: 1, background: T.rule, margin: '6px 0' }} />
      ) : (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
          <span style={{ display: 'flex', gap: 3, minWidth: isTouch ? 90 : 110 }}>
            {r.keys.map((k, j) => (
              <span key={j} style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}>
                {j > 0 && <span style={{ color: T.textDim, fontSize: 10 }}>+</span>}
                <kbd style={{
                  display: 'inline-block', minWidth: 18, padding: '2px 6px',
                  border: `1px solid ${T.border}`, borderRadius: 3,
                  fontFamily: T.font, fontSize: 10.5, fontWeight: 500,
                  background: 'rgba(127,127,127,.10)', color: T.text, textAlign: 'center',
                }}>{k}</kbd>
              </span>
            ))}
          </span>
          <span style={{ color: T.textDim, fontSize: 11 }}>{r.label}</span>
        </div>
      ))}
    </div>
  );
}
